import { Box, Flex, Heading, Icon, IconButton, Spinner, Text, Textarea, VStack } from "@chakra-ui/react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowUp, FiCheck, FiX } from "react-icons/fi";
import { TipTap } from "../input/TipTap/TipTap";
import { getTiptapJsonFromMarkdown } from "../input/TipTap/markdownUtils";
import { useAssistantContext } from "./AssistantProvider";
import { AssistantEmptyState } from "./AssistantEmptyState";
import { ConfirmationCard } from "./ConfirmationCard";
import { ConversationSwitcher } from "./ConversationSwitcher";
import { DocumentSourceCards } from "./DocumentSourceCards";
import { useProcurementTerms } from "../terminology/useProcurementTerms";
import { EntityHighlightCards } from "./EntityHighlightCards";
import { ModelSwitcher } from "./ModelSwitcher";
import { nextRevealText } from "./textReveal";

const COMPOSER_MAX_HEIGHT_PX = 160;

const REVEAL_TICK_MS = 40;

// Reveal a live-streamed text at a steady word cadence instead of the network's lumpy chunking —
// the word-for-word feel is an animation on top of the stream. Inactive (settled/restored) turns
// snap straight to the full text.
const useSmoothedText = (target: string, active: boolean): string => {
  const [displayed, setDisplayed] = useState(target);
  const targetRef = useRef(target);
  targetRef.current = target;
  const hasTarget = target !== "";

  useEffect(() => {
    if (!active) {
      setDisplayed(targetRef.current);
      return;
    }
    const handle = setInterval(() => {
      setDisplayed((current) => nextRevealText(current, targetRef.current));
    }, REVEAL_TICK_MS);
    return () => clearInterval(handle);
  }, [active, hasTarget]);

  return active ? displayed : target;
};

// The one answer renderer: streamed text re-parses through the same read-only rich-text path a
// restored turn uses, so live and restored answers are identical. While the turn is live the text
// runs through the typewriter reveal.
const AssistantAnswer = ({ text, streaming }: { text: string; streaming: boolean }) => {
  const smoothed = useSmoothedText(text, streaming);
  const content = useMemo(() => getTiptapJsonFromMarkdown(smoothed), [smoothed]);
  return (
    <Box
      w="full"
      sx={{ ".tiptap p": { marginBottom: "0.75rem" }, ".tiptap p:last-child": { marginBottom: 0 } }}
    >
      <TipTap content={content} editable={false} hasBorder={false} padding="0" maxHeight="full" />
    </Box>
  );
};

export const AssistantChat = () => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const {
    turns,
    isRunning,
    activeStep,
    pendingConfirmation,
    confirm,
    cancel,
    refine,
    lookupEntity,
    navigateToEntity,
    send,
    draft,
    setDraft,
  } = useAssistantContext();
  const bottomRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns, isRunning, activeStep, pendingConfirmation]);

  useEffect(() => {
    const composer = composerRef.current;
    if (!composer) {
      return;
    }
    composer.style.height = "auto";
    composer.style.height = `${Math.min(composer.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [draft]);

  const submit = () => {
    const text = draft.trim();
    if (!text) {
      return;
    }
    // While a confirmation card is up, a new message refines the proposal instead of being blocked.
    if (pendingConfirmation) {
      setDraft("");
      refine(text);
      return;
    }
    if (isRunning) {
      return;
    }
    setDraft("");
    void send(text);
  };

  const sendPrompt = (text: string) => {
    if (isRunning) {
      return;
    }
    setDraft("");
    void send(text);
  };

  return (
    <Flex direction="column" h="full" w="full" maxW="4xl" mx="auto">
      <Flex align="flex-start" justify="space-between" gap="3" mb="1">
        <Heading size="md" color="smText">
          {t("The assistant")}
        </Heading>
        <Flex align="center" gap="1">
          <ModelSwitcher />
          <ConversationSwitcher />
        </Flex>
      </Flex>
      <Text fontSize="sm" color="smTextDisabled" mb="4">
        {terms.assistantAskPlaceholder}
      </Text>

      <Box
        flex="1"
        minH="0"
        overflowY="auto"
        bg="smBackground"
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        p="4"
      >
        <VStack align="stretch" spacing="4">
          {turns.length === 0 && <AssistantEmptyState onPrompt={sendPrompt} />}

          {turns.map((turn, i) => {
            return (
              <Fragment key={i}>
                <Flex justify="flex-end">
                  <Box
                    maxW="75%"
                    px="4"
                    py="2"
                    borderRadius="16px 16px 4px 16px"
                    bg="smPrimary"
                    color="white"
                    fontSize="sm"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                  >
                    {turn.user}
                  </Box>
                </Flex>

                {turn.steps.map((step, j) => (
                  <Flex key={j} align="center" gap="2" color="smTextDisabled" fontSize="xs">
                    <Icon as={step.ok ? FiCheck : FiX} color={step.ok ? "smPrimary" : "red.400"} />
                    <Text>{step.label}</Text>
                  </Flex>
                ))}

                {turn.note && (
                  <Text fontSize="sm" color="smTextDisabled">
                    {turn.note}
                  </Text>
                )}

                {turn.answerText && (
                  <AssistantAnswer text={turn.answerText} streaming={isRunning && i === turns.length - 1} />
                )}

                {turn.entities && turn.entities.length > 0 && (
                  <EntityHighlightCards entities={turn.entities} lookupEntity={lookupEntity} onOpen={navigateToEntity} />
                )}

                {turn.sources && turn.sources.length > 0 && <DocumentSourceCards sources={turn.sources} />}
              </Fragment>
            );
          })}

          {pendingConfirmation && (
            <ConfirmationCard
              pending={pendingConfirmation}
              lookupEntity={lookupEntity}
              onConfirm={confirm}
              onCancel={cancel}
              isBusy={false}
            />
          )}

          {isRunning && activeStep && (
            <Flex align="center" gap="2" color="smTextDisabled" fontSize="sm">
              <Spinner size="xs" />
              <Text>{`${activeStep}…`}</Text>
            </Flex>
          )}

          <div ref={bottomRef} />
        </VStack>
      </Box>

      <Flex
        align="flex-end"
        gap="2"
        mt="3"
        flexShrink={0}
        bg="smBackground"
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="24px"
        px="3"
        py="2"
      >
        <Textarea
          ref={composerRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={(pendingConfirmation ? t("Adjust, or use the buttons above …") : t("Write a message …")) ?? ""}
          isDisabled={isRunning && !pendingConfirmation}
          variant="unstyled"
          rows={1}
          resize="none"
          minH="40px"
          maxH={`${COMPOSER_MAX_HEIGHT_PX}px`}
          overflowY="auto"
          px="2"
          py="2"
          flex="1"
        />
        <IconButton
          aria-label={t("Send") ?? "Send"}
          icon={<FiArrowUp />}
          onClick={submit}
          isRound
          bg="smPrimary"
          color="white"
          _hover={{ bg: "smPrimarySolid" }}
          flexShrink={0}
          isDisabled={(isRunning && !pendingConfirmation) || !draft.trim()}
        />
      </Flex>
    </Flex>
  );
};
